import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Container, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import PageContainer from "@components/layout/PageContainer";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import DefaultOffCanvas from "@components/layout/Offcanvas/DefaultOffCanvas";
import LoadingScreen from "@components/content/LoadingScreen";
import AdminTenantUserForm from "@components/forms/AdminTenantUserForm";
import { faEdit, faEnvelope, faKey, faTrash } from "@fortawesome/free-solid-svg-icons";
import { showAlert } from "@utils/Alert";
import { ApiClient } from "@/api/ApiClient";
import { UserApi, UserDataType } from "@/api/UserApi";
import { RoleApi } from "@/api/RoleApi";
import { TenantApi } from "@/api/TenantApi";
import { AdminTenantUserFormData } from "@/formTypes/AdminTenantUserFormDataType";
import styles from "./style.module.scss";

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/;

const TenantUsersIndex = () => {
    const { id: tenantRecordId } = useParams<{ id: string }>();
    const modelRef = useRef<ModelIndexHandle>(null);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [targetTenantId, setTargetTenantId] = useState<string>("");
    const [tenantName, setTenantName] = useState<string>("");
    const [roles, setRoles] = useState<Record<string, string>>({});

    const [formOpen, setFormOpen] = useState<boolean>(false);
    const [editingUser, setEditingUser] = useState<AdminTenantUserFormData | undefined>(undefined);

    const [passwordOpen, setPasswordOpen] = useState<boolean>(false);
    const [passwordUser, setPasswordUser] = useState<UserDataType | undefined>(undefined);
    const [newPassword, setNewPassword] = useState<string>("");

    useEffect(() => {
        loadTenantData();
    }, [tenantRecordId]);

    const loadTenantData = async () => {
        if (!tenantRecordId) return;
        setIsLoading(true);
        try {
            const tenant = await TenantApi.get(tenantRecordId);
            const ownerId = `${tenant.tenant || ""}`;
            setTargetTenantId(ownerId);
            setTenantName(`${tenant.nombre || ""}`);
            if (ownerId) {
                await syncOwnerUser(ownerId, `${tenant.email || ""}`, `${tenant.nombre || ""}`);
                const tenantRoles = await RoleApi.optionsListByTenantId(ownerId);
                setRoles(tenantRoles);
            }
        } catch (err) {
            showAlert("danger", "No fue posible cargar la suscripción");
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Backfill del dueno de la suscripcion: las cuentas creadas antes de que el provisioning
     * escribiera su fila `User` no lo tienen en la tabla, asi que no aparece en esta lista ni
     * en la del propio tenant. La lambda es idempotente, por eso se llama en cada carga.
     * Best-effort: un fallo no debe impedir ver la pantalla.
     */
    const syncOwnerUser = async (ownerId: string, email: string, nombre: string) => {
        if (!email) return;
        try {
            await ApiClient.mutations.adminTenantUserMutation({
                action: "syncOwner",
                tenantId: ownerId,
                email,
                name: nombre,
            });
        } catch (err) {
            console.error("[TenantUsers] No fue posible sincronizar el dueño", err);
        }
    };

    const refreshList = () => {
        setTimeout(() => modelRef.current?.refetch(), 300);
    };

    const openCreate = () => {
        setEditingUser(undefined);
        setFormOpen(true);
    };

    const openEdit = (user: UserDataType) => {
        setEditingUser({
            id: user.id,
            name: user.name,
            email: user.email,
            roleId: user.roleId,
        });
        setFormOpen(true);
    };

    const onUserStored = () => {
        setFormOpen(false);
        setEditingUser(undefined);
        refreshList();
    };

    const openChangePassword = (user: UserDataType) => {
        setPasswordUser(user);
        setNewPassword("");
        setPasswordOpen(true);
    };

    const submitChangePassword = async () => {
        if (!PASSWORD_REGEX.test(newPassword)) {
            showAlert("danger", "La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un símbolo");
            return;
        }
        setIsLoading(true);
        try {
            const { data: res } = await ApiClient.mutations.adminTenantUserMutation({
                action: "setPassword",
                username: `${passwordUser?.email || ""}`,
                password: newPassword,
            });
            if (!res?.ok) throw Error(res?.error || "No fue posible cambiar la contraseña");
            showAlert("success", "La contraseña fue actualizada correctamente");
            setPasswordOpen(false);
            setPasswordUser(undefined);
            setNewPassword("");
        } catch (err) {
            showAlert("danger", (err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    const sendResetPassword = async (user: UserDataType) => {
        setIsLoading(true);
        try {
            const { data: res } = await ApiClient.mutations.adminTenantUserMutation({
                action: "resetPassword",
                username: `${user.email || ""}`,
            });
            if (!res?.ok) throw Error(res?.error || "No fue posible enviar el correo de reset");
            showAlert("success", "Se envió el correo de restablecimiento de contraseña");
        } catch (err) {
            showAlert("danger", (err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteUser = async (user: UserDataType) => {
        if (user.isOwner) {
            showAlert("danger", "No se puede eliminar al dueño de la suscripción");
            return;
        }
        setIsLoading(true);
        try {
            const { data: res } = await ApiClient.mutations.adminTenantUserMutation({
                action: "delete",
                id: `${user.id || ""}`,
                username: `${user.email || ""}`,
            });
            if (!res?.ok) throw Error(res?.error || "No fue posible eliminar el usuario");
            showAlert("success", "El usuario fue eliminado correctamente");
            refreshList();
        } catch (err) {
            showAlert("danger", (err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageContainer
            title={`Usuarios de ${tenantName || "Suscripción"}`}
            backUrl="/admin/suscriptions"
            primaryActions={[
                {
                    onClick: openCreate,
                    actionTitle: "Nuevo Usuario",
                    color: "success",
                },
            ]}
        >
            {targetTenantId && (
                <ModelIndex<UserDataType>
                    ref={modelRef}
                    messages={{ noResults: "Esta suscripción no tiene usuarios aún" }}
                    fetchData={(filters?: any) => UserApi.fetchByTenantId(targetTenantId, filters)}
                    filterProps={[{ key: "name", filterType: "contains" }]}
                    secondaryProperties={[
                        {
                            key: (data: UserDataType) => roles[data.roleId || ""] || "",
                            highlighted: true,
                            title: "Rol",
                        },
                        { key: (data: UserDataType) => data.name || "", title: "Nombre" },
                        { key: "email", title: "Email" },
                    ]}
                    secondaryPropertiesSizing={[
                        { sm: "2", md: "2" },
                        { sm: "3", md: "4" },
                        { sm: "3", md: "4" },
                    ]}
                    listActions={[
                        {
                            actionTitle: "Editar Usuario",
                            iconProps: { icon: faEdit },
                            onClick: (_id: string, item: UserDataType) => openEdit(item),
                        },
                        {
                            actionTitle: "Cambiar Contraseña",
                            iconProps: { icon: faKey },
                            onClick: (_id: string, item: UserDataType) => openChangePassword(item),
                        },
                        {
                            actionTitle: "Enviar Reset de Contraseña",
                            iconProps: { icon: faEnvelope },
                            needConfirm: true,
                            confirmProps: {
                                title: "Enviar Reset de Contraseña",
                                confirmMessage: "¿Enviar un correo de restablecimiento de contraseña a este usuario?",
                                confirmBtnText: "Enviar",
                            },
                            onClick: (_id: string, item: UserDataType) => sendResetPassword(item),
                        },
                        {
                            actionTitle: "Eliminar Usuario",
                            iconProps: { icon: faTrash },
                            needConfirm: true,
                            confirmProps: {
                                title: "Eliminar Usuario",
                                confirmMessage: "¿Seguro que desea eliminar este usuario?",
                                confirmBtnText: "Eliminar",
                            },
                            // El dueno de la suscripcion no se puede eliminar: dejaria la
                            // cuenta sin acceso.
                            isVisible: (item: UserDataType) => !item.isOwner,
                            onClick: (_id: string, item: UserDataType) => deleteUser(item),
                        },
                    ]}
                />
            )}

            <DefaultOffCanvas
                className={styles.offCanvas}
                direction="end"
                isOpen={formOpen}
                onClosed={() => setFormOpen(false)}
                toggle={() => setFormOpen(!formOpen)}
            >
                <OffcanvasHeader className={styles.offCanvasHeader} toggle={() => setFormOpen(false)}>
                    {editingUser?.id ? "Editar Usuario" : "Nuevo Usuario"}
                </OffcanvasHeader>
                <OffcanvasBody>
                    <Container>
                        {formOpen && (
                            <AdminTenantUserForm
                                formId="adminTenantUserForm"
                                targetTenantId={targetTenantId}
                                roles={roles}
                                defaultData={editingUser}
                                onStored={onUserStored}
                            />
                        )}
                    </Container>
                </OffcanvasBody>
                <div className={styles.footerButtons}>
                    <Button color="primary" form="adminTenantUserForm">
                        Guardar
                    </Button>
                </div>
            </DefaultOffCanvas>

            <DefaultOffCanvas
                className={styles.offCanvas}
                direction="end"
                isOpen={passwordOpen}
                onClosed={() => setPasswordOpen(false)}
                toggle={() => setPasswordOpen(!passwordOpen)}
            >
                <OffcanvasHeader className={styles.offCanvasHeader} toggle={() => setPasswordOpen(false)}>
                    Cambiar Contraseña
                </OffcanvasHeader>
                <OffcanvasBody>
                    <Container>
                        <p>Usuario: <strong>{passwordUser?.email}</strong></p>
                        <label htmlFor="newPassword">Nueva contraseña</label>
                        <input
                            id="newPassword"
                            type="password"
                            className="form-control"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                        />
                        <small className="text-muted">
                            Mínimo 8 caracteres, una mayúscula, una minúscula, un número y un símbolo.
                        </small>
                    </Container>
                </OffcanvasBody>
                <div className={styles.footerButtons}>
                    <Button color="primary" type="button" onClick={submitChangePassword}>
                        Guardar
                    </Button>
                </div>
            </DefaultOffCanvas>

            <LoadingScreen isLoading={isLoading} />
        </PageContainer>
    );
};

export default TenantUsersIndex;
